import React from 'react';
import { Box, type SxProps, type Theme } from '@mui/material';

export interface OverlayProps {
  /** Overlay variant */
  variant?: 'dark' | 'light';
  /** Opacity (0-1, default: 0.7) */
  opacity?: number;
  /** Border radius (default: 0) */
  borderRadius?: number | string;
  /** Whether the overlay is clickable */
  onClick?: () => void;
  /** Content to display on the overlay */
  children?: React.ReactNode;
  /** Additional sx props */
  sx?: SxProps<Theme>;
}

/**
 * Overlay - Atomic component for semi-transparent overlays
 * 
 * Use cases:
 * - Compliance overlays (sign-in required, terms of use)
 * - Modal backdrops
 * - Image overlays with text
 * 
 * @example
 * ```tsx
 * <Overlay variant="dark" onClick={handleClick}>
 *   <Text>Sign in to view</Text>
 * </Overlay>
 * ```
 */
export function Overlay({
  variant = 'dark',
  opacity = 0.7,
  borderRadius = 0,
  onClick,
  children,
  sx,
}: OverlayProps) {
  const backgroundColor = variant === 'dark' 
    ? `rgba(0, 0, 0, ${opacity})` 
    : `rgba(255, 255, 255, ${opacity})`;

  const textColor = variant === 'dark' ? 'white' : 'black';

  return (
    <Box
      onClick={onClick}
      sx={{
        position: 'absolute',
        inset: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor,
        borderRadius,
        color: textColor,
        textAlign: 'center',
        lineHeight: '1.5em',
        cursor: onClick ? 'pointer' : 'default',
        zIndex: 10,
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

export default Overlay;
